import BaseExtension from './base';

import { createElement, addClass, removeClass, addEvent, removeEvent, each } from '../utils';

import opener from './helpers/opener';

import { storeData, getStoredData } from './helpers/storage';
import createSvg, { MUTED, VOLUME_UP } from '../svgs';

const CN = 'rmp-ext-volume';

const BUTTON_CN = `${CN}__btn`;
const MUTED_CN = `${BUTTON_CN}--muted`;

const SELECTOR_CN = `${CN}__selector`;
const SELECTOR_OPEN_CN = `${SELECTOR_CN}--open`;
const SELECTOR_ACTIVE_CN = `${SELECTOR_CN}--active`;

const SLIDER_CN = `${CN}__slider`;
const HANDLE_CN = `${CN}__handle`;
const PROGRESS_CN = `${CN}__progress`;
const OVERLAY_CN = `${CN}__overlay`;

const VOLUME_STORAGE_KEY = 'volume';
const MUTED_STORAGE_KEY = 'muted';

const MIN_VOLUME = 0.1;

const ESC_CODE    = 27;

export default class VolumeExtension extends BaseExtension {
   _dragNdropTokens = [];

   constructor(player, state, options) {
      super(player, state, options, ['volumechange', 'loadedmetadata']);
      this.logger.debug('volume:extension:creating');

      this.onHandleMouseUp = this.onHandleMouseUp.bind(this);
      this.onHandleMouseDown = this.onHandleMouseDown.bind(this);
      this.onHandleMouseMove = this.onHandleMouseMove.bind(this);
      this.onHandleEsqKey = this.onHandleEsqKey.bind(this);

      this.render();

      this.addEvent(this.$button, 'click', this.toggle.bind(this));
      this.addEvent(this.$slider, 'click', this.changeVolume.bind(this));
      this.addEvent(this.$handle, 'mousedown', this.onHandleMouseDown);

      opener(this, { $button: this.$button, $selector: this.$selector, SELECTOR_OPEN_CN, closeOnClick: false });

      this.refreshHandle();
      this.logger.debug('volume:extension:created');
   }

   render() {
      /** @type {HTMLMediaElement} */
      this.$el = createElement('span', { class: CN });
      this.$button = createElement('button', { type: 'button', class: BUTTON_CN }, this.$el);
      this.$button.setAttribute('aria-label', 'Volume');
      this.$button.addEventListener('keydown', this.onHandleEsqKey);

      this.$selector = createElement('div', { class: SELECTOR_CN }, this.$el);

      this.$slider = createElement('span', { class: SLIDER_CN }, this.$selector);
      createElement('span', { class: OVERLAY_CN }, this.$slider);
      this.$progress = createElement('span', { class: PROGRESS_CN }, this.$slider);
      this.$handle = createElement('span', { class: HANDLE_CN }, this.$slider);

      createSvg(this.$button, MUTED, { class: 'muted' });
      createSvg(this.$button, VOLUME_UP, { class: 'unmuted' });
   }

   toggle() {
      const muted = !this.player.isMuted();
      this.player.setMuted(muted);
      storeData(MUTED_STORAGE_KEY, muted);

      if (!muted) {
         const volume = Math.max(this.player.getVolume(), MIN_VOLUME);
         this.player.setVolume(volume);
         storeData(VOLUME_STORAGE_KEY, volume);
      }
   }

   _getVolumeByEvent(e) {
      const rect = this.$slider.getBoundingClientRect();
      return (1 - (clientY(e) - rect.top) / this.$slider.offsetHeight).toFixed(2);
   }

   changeVolume(e) {
      const volume = this._getVolumeByEvent(e);
      this.updateVolume(volume);
   }

   updateVolume(volume) {
      volume = _normalizeVolume(volume);
      this.logger.debug('changevolume', volume);
      if (volume) {
         this.player.setVolume(volume);
         this.player.setMuted(false);
         storeData(VOLUME_STORAGE_KEY, volume);
         storeData(MUTED_STORAGE_KEY, false);
      } else {
         this.player.setMuted(true);
         storeData(MUTED_STORAGE_KEY, true);
      }
   }

   onVolumechange() {
      if (this.player.isMuted()) {
         addClass(this.$button, MUTED_CN);
      } else {
         removeClass(this.$button, MUTED_CN);
      }
      this.refreshHandle();
   }

   onLoadedmetadata() {
      const muted = this.options.muted;
      this.player.setVolume(parseFloat(getStoredData(VOLUME_STORAGE_KEY, '1.0')));
      if (typeof muted === 'boolean') {
         this.player.setMuted(muted);
      } else {
         this.player.setMuted(getStoredData(MUTED_STORAGE_KEY, 'false') === 'true', true);
      }
   }

   refreshHandle() {
      this._refreshHadle4volume(this.player.getVolume());
   }

   _refreshHadle4volume(volume) {
      const position = this.player.isMuted() ? '100%' : (1 - volume) * 100 + '%';
      this.$handle.style.top = this.$progress.style.top = position;
   }

   onHandleMouseDown() {
      addClass(this.$selector, SELECTOR_ACTIVE_CN);
      this._dragNdropTokens.push( addEvent(document, 'mouseup', this.onHandleMouseUp) );
      this._dragNdropTokens.push( addEvent(document, 'mousemove', this.onHandleMouseMove) );
   }

   onHandleMouseUp(e) {
      each(this._dragNdropTokens, removeEvent);
      this._dragNdropTokens.length = 0;
      this.changeVolume(e);
      removeClass(this.$selector, SELECTOR_ACTIVE_CN);
   }

   onHandleMouseMove(e) {
      let volume = this._getVolumeByEvent(e);
      volume = _normalizeVolume(volume);
      this._refreshHadle4volume(volume);
   }

   onHandleEsqKey(e) {
      const keyCode = e.keyCode;
      if (keyCode === ESC_CODE) {
         removeClass(this.$selector, SELECTOR_OPEN_CN);
         e.preventDefault();
      }
   }

   getElement() {
      return this.$el;
   }

   destroy() {
      super.destroy();

      const parent = this.$el.parentNode;

      parent.removeChild(this.$el);

      if (this.$button) {
         this.$button.removeEventListener('keydown', this.onHandleEsqKey);
      }

      this.$el = null;
      this.$button = null;
      this.$selector = null;
      this.$slider = null;
      this.$progress = null;
      this.$handle = null;
   }
}

function clientY(e) {
   return e.clientY;
}

function _normalizeVolume(volume) {
   volume = Math.max(0, volume);
   volume = Math.min(volume, 1);
   return volume;
}
